import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { type ReactElement } from "react";

import { LedgerAmount } from "~/components/ledger_amount";
import { useConfig } from "~/contexts/config";
import { type Money } from "~/entities/money";
import { isAdminTransaction, TransactionMoney } from "~/entities/transaction";
import { getPaymentURL } from "~/services/stripe";

import { StripePaymentStatus } from "./StripePaymentStatus";

export function BuyerLedger({
  transactionData,
}: {
  transactionData: Parameters<typeof isAdminTransaction>[0];
}) {
  const { stripe: stripe, localization } = useConfig();

  const isAdminTx = isAdminTransaction(transactionData);

  const transactionMoney = new TransactionMoney(transactionData);

  const stripePaymentIntentId = isAdminTx
    ? transactionData.deposit_stripe_payment_intent_id ||
      transactionData.stripe_payment_intent_id
    : undefined;

  const stripePlatformId = isAdminTx
    ? transactionData.stripe_platform_id
    : undefined;

  const remainderStripePaymentIntentId = isAdminTx
    ? transactionData.remainder_stripe_payment_intent_id
    : undefined;

  const paidDateTime = transactionData?.deposit_paid || transactionData?.paid;
  const remainderPaidDateTime = transactionData?.remainder_paid;

  const isDepositInReview = isAdminTx
    ? transactionData.deposit_in_review
    : false;

  const isRemainderInReview = isAdminTx
    ? transactionData.remainder_in_review
    : false;

  const isPaymentInReview = isAdminTx
    ? transactionData.payment_in_review
    : false;

  const depositOutcomeMessage = isAdminTx
    ? transactionData.deposit_review?.outcome_reason
    : undefined;

  const remainderOutcomeMessage = isAdminTx
    ? transactionData.remainder_review?.outcome_reason
    : undefined;

  const paymentOutcomeMessage = isAdminTx
    ? transactionData.payment_review?.outcome_reason
    : undefined;

  const isDepositPaymentReviewRejected =
    isAdminTx &&
    !!transactionData.deposit_review &&
    !transactionData.deposit_review.approved;

  const isRemainderPaymentReviewRejected =
    isAdminTx &&
    !!transactionData.remainder_review &&
    !transactionData.remainder_review.approved;

  const isPaymentReviewRejected =
    isAdminTx &&
    !!transactionData.payment_review &&
    !transactionData.payment_review.approved;
  const isPaid = !!paidDateTime;
  const isRemainderPaid = !!remainderPaidDateTime;
  const isCancelled = !!transactionData.cancelled;

  const renderPaidComponent = () => {
    let paidHeading = "Expected Payment";
    if (isPaid) {
      paidHeading = `Paid ${paidDateTime.toLocaleString(localization.locale, { timeZone: localization.timeZone }).split(", ")[0]}`;
    }

    return (
      <>
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mb: 1,
              pb: 0.5,
              borderBottom: "1px dashed",
              borderColor: "components.input.outlined.enabledBorder",
            }}
          >
            <Typography variant="body1" gutterBottom>
              {paidHeading}
            </Typography>
            <Stack direction="row" spacing={3} alignItems="center">
              {stripePaymentIntentId && (
                <Link
                  href={getPaymentURL(
                    stripe,
                    stripePlatformId,
                    stripePaymentIntentId,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="buyer-ledger-stripe-link"
                >
                  Stripe Payment Intent
                </Link>
              )}
            </Stack>
          </Stack>
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="body2">Price</Typography>
            <LedgerAmount
              amount={transactionMoney.getPrice()}
              data-testid="buyer-ledger-price-text"
            />
          </Stack>
          {!transactionMoney.getPriceExtra().isZero() && (
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="body2">Price (extra)</Typography>
              <LedgerAmount
                amount={transactionMoney.getPriceExtra()}
                data-testid="buyer-ledger-price_extra-text"
              />
            </Stack>
          )}
          {transactionMoney.getPostageBearer() === "buyer" &&
            !transactionMoney.getPricePostage().isZero() && (
              <Stack flexDirection="row" justifyContent="space-between">
                <Typography variant="body2">Postage fee</Typography>
                <LedgerAmount
                  amount={transactionMoney.getPricePostage()}
                  data-testid="buyer-ledger-price_postage-text"
                />
              </Stack>
            )}
          {!transactionMoney.getCharge().isZero() && (
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="body2">Trustap fee</Typography>
              <LedgerAmount
                amount={transactionMoney.getCharge()}
                data-testid="buyer-ledger-charge-text"
              />
            </Stack>
          )}
          {!transactionMoney.getChargeBuyerClient().isZero() && (
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="body2">Platform commission</Typography>
              <LedgerAmount
                amount={transactionMoney.getChargeBuyerClient()}
                data-testid="buyer-ledger-charge_buyer_client-text"
              />
            </Stack>
          )}
          {!transactionMoney.getChargeBuyerPostage().isZero() && (
            <Stack flexDirection="row" justifyContent="space-between">
              <Typography variant="body2">Postage fee</Typography>
              <LedgerAmount
                amount={transactionMoney.getChargeBuyerPostage()}
                data-testid="buyer-ledger-charge_postage_buyer-text"
              />
            </Stack>
          )}

          {stripePaymentIntentId && stripePlatformId && (
            <StripePaymentStatus
              outcomeMessage={paymentOutcomeMessage || depositOutcomeMessage}
              isInReview={isDepositInReview || isPaymentInReview}
              isPaymentRejected={
                isDepositPaymentReviewRejected || isPaymentReviewRejected
              }
              paymentIntentId={stripePaymentIntentId}
              stripePlatformId={stripePlatformId}
              isPaid={isPaid}
              isCancelled={isCancelled}
            />
          )}
        </Stack>
      </>
    );
  };

  const renderRemainderComponent = () => {
    return (
      <Stack spacing={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mb: 1,
            pb: 0.5,
            borderBottom: "1px dashed",
            borderColor: "components.input.outlined.enabledBorder",
          }}
        >
          <Typography variant="body1" gutterBottom>
            Remainder{" "}
            {remainderPaidDateTime?.toLocaleString(localization.locale, {
              timeZone: localization.timeZone,
            }) ?? ""}
          </Typography>
          <Stack direction="row" spacing={3} alignItems="center">
            {remainderStripePaymentIntentId && (
              <Link
                href={getPaymentURL(
                  stripe,
                  stripePlatformId,
                  remainderStripePaymentIntentId,
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="buyer-ledger-remainder-stripe-link"
              >
                Stripe
              </Link>
            )}
          </Stack>
        </Stack>
        <Stack flexDirection="row" justifyContent="space-between">
          <Typography variant="body2">Price</Typography>
          <LedgerAmount
            amount={transactionMoney.getRemainderPrice()}
            data-testid="buyer-ledger-remainder-price-text"
          />
        </Stack>
        <Stack flexDirection="row" justifyContent="space-between">
          <Typography variant="body2">Buyer fees</Typography>
          <LedgerAmount
            amount={transactionMoney.getRemainderCharge()}
            data-testid="buyer-ledger-remainder-fees-text"
          />
        </Stack>
        {stripePlatformId && remainderStripePaymentIntentId && (
          <StripePaymentStatus
            outcomeMessage={remainderOutcomeMessage}
            isInReview={isRemainderInReview}
            isPaymentRejected={isRemainderPaymentReviewRejected}
            paymentIntentId={remainderStripePaymentIntentId}
            stripePlatformId={stripePlatformId}
            isPaid={isPaid}
            isCancelled={isCancelled}
          />
        )}
      </Stack>
    );
  };

  const renderRefundComponent = (refund: {
    id: string;
    amount: Money;
    refunded: Date;
    isPostRelease: boolean;
  }) => {
    return (
      <Stack spacing={1} key={refund.id}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            mb: 1,
            pb: 0.5,
            borderBottom: "1px dashed",
            borderColor: "components.input.outlined.enabledBorder",
          }}
        >
          <Typography variant="body1" gutterBottom>
            Refund{" "}
            {
              refund.refunded
                .toLocaleString(localization.locale, {
                  timeZone: localization.timeZone,
                })
                .split(", ")[0]
            }
          </Typography>
          {stripePaymentIntentId && (
            <Stack direction="row" spacing={3} alignItems="center">
              <Link
                href={getPaymentURL(
                  stripe,
                  stripePlatformId,
                  stripePaymentIntentId,
                )}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="buyer-ledger-refund-stripe-link"
              >
                Stripe Refund
              </Link>
            </Stack>
          )}
        </Stack>
        <Stack spacing={1}>
          <Stack flexDirection="row" justifyContent="space-between">
            <Typography variant="body2">
              Refund
              {refund.isPostRelease ? " (post release)" : ""}
            </Typography>
            <LedgerAmount
              amount={refund.amount.negate()}
              data-testid="buyer-ledger-amount_refunded-text"
            />
          </Stack>
        </Stack>
      </Stack>
    );
  };

  const renderLedgerComponentsChronologically = () => {
    const paymentComponents: {
      timestamp: Date;
      component: ReactElement | null;
    }[] = [];

    paymentComponents.push({
      timestamp: new Date(0, 0, 0),
      component: renderPaidComponent(),
    });

    if (isRemainderPaid) {
      const remainderComponent = renderRemainderComponent();
      if (remainderComponent) {
        paymentComponents.push({
          timestamp: remainderPaidDateTime,
          component: remainderComponent,
        });
      }
    }

    const refunds = transactionMoney.getRefunds();
    refunds.forEach((refund) => {
      paymentComponents.push({
        timestamp: refund.refunded,
        component: renderRefundComponent(refund),
      });
    });

    const refunded = isAdminTx
      ? transactionData.payment_refunded || transactionData.deposit_refunded
      : transactionData.deposit_refunded || transactionData.payment_refunded;

    if (refunded && refunds.length === 0) {
      paymentComponents.push({
        timestamp: refunded,
        component: renderRefundComponent({
          refunded: refunded,
          amount: transactionMoney.getAmountRefunded(),
          id: "",
          isPostRelease: false,
        }),
      });
    }

    paymentComponents.sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
    );

    return paymentComponents.map((item, index) => (
      <div key={index}>{item.component}</div>
    ));
  };

  let paymentLabel = "Expected Payment";
  let paymentValue = transactionMoney.getBuyerPaid();
  if (isPaid) {
    paymentLabel = "Buyer Paid Balance";
  } else if (isCancelled) {
    paymentLabel = "Buyer Paid Balance";
    paymentValue = transactionMoney.zero();
  }

  return (
    <Stack
      sx={{
        backgroundColor: "background.default",
        border: "1px solid",
        borderColor: "gray.200",
        borderRadius: 2,
        p: 3,
      }}
      flexDirection="column"
      spacing={3}
    >
      {renderLedgerComponentsChronologically()}
      <Stack flexDirection="row" justifyContent="space-between">
        <Typography variant="body2">{paymentLabel}</Typography>
        <LedgerAmount
          amount={paymentValue}
          variant="sumUpLine"
          data-testid="buyer-paid-balance-text"
        />
      </Stack>
    </Stack>
  );
}
